/**
 * CoachDIA Discovery — COACHDIA Pulse MVP.
 *
 * Analyse multi-profils : répartition des énergies, équilibre, risques,
 * complémentarités et leviers de collaboration.
 *
 * MVP : mode démo (choix d'archétypes) + mode owner-sessions (backlog).
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Users, Plus, Minus, Loader2, ArrowRight, TrendingUp,
  AlertTriangle, Sparkles, Link2, Compass, Home, ArrowLeft, Lock, Check, Download,
  Mail, Send, Zap, Flame, Eye,
} from 'lucide-react';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';

const API = getApiUrl();
const COLORS = ['red', 'yellow', 'green', 'blue'];
const COLOR_META = {
  red:    { hex: '#C0392B', label: 'Action' },
  yellow: { hex: '#D4A017', label: 'Inspiration' },
  green:  { hex: '#2D8659', label: 'Harmonie' },
  blue:   { hex: '#1E4F8C', label: 'Analyse' },
};

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ─── Distribution Bar ───
const DistributionBar = ({ distribution }) => (
  <div className="space-y-2.5">
    {COLORS.map(c => {
      const val = distribution?.[c] || 0;
      return (
        <div key={c} className="flex items-center gap-3">
          <div className="w-24 text-xs tracking-wider uppercase text-stone-500">{COLOR_META[c].label}</div>
          <div className="flex-1 bg-stone-100 h-5 rounded-sm overflow-hidden">
            <div className="h-full rounded-sm" style={{ backgroundColor: COLOR_META[c].hex, width: `${Math.min(100, val)}%` }} />
          </div>
          <div className="w-10 text-right text-sm font-semibold" style={{ color: COLOR_META[c].hex }}>{Math.round(val)}</div>
        </div>
      );
    })}
  </div>
);

// ─── Composer — demo archetype picker ───
const ArchetypePicker = ({ selected, onAdd, onRemove }) => (
  <div className="grid grid-cols-4 gap-2">
    {COLORS.map(c => (
      <button
        key={c}
        data-testid={`team-insights-add-${c}`}
        onClick={() => onAdd(c)}
        className="p-3 border border-stone-300 hover:border-[#92710A] rounded-sm text-left transition-all"
      >
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLOR_META[c].hex }} />
          <span className="text-[10px] tracking-widest uppercase text-stone-500">{COLOR_META[c].label}</span>
        </div>
        <Plus className="w-3 h-3 text-stone-400 mt-2" />
      </button>
    ))}
  </div>
);

export default function TeamInsights() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Iter234 — mode démo supprimé. Flow strict : email collab → preview verrouillée → paiement → rapport.
  const [emailsInput, setEmailsInput] = useState('');
  const [memberNames, setMemberNames] = useState([]);
  const [preview, setPreview] = useState(null);
  const [unlockedReport, setUnlockedReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailMessage, setEmailMessage] = useState('');

  const parsedEmails = useMemo(() => {
    return emailsInput
      .split(/[\s,;\n]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e.includes('@'));
  }, [emailsInput]);

  // Si user revient d'un checkout Stripe réussi, on charge le rapport déverrouillé
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const unlock = params.get('unlock');
    const reportId = params.get('report_id');
    if (unlock === 'success' && reportId) {
      (async () => {
        setLoading(true);
        try {
          // Petit délai pour laisser le webhook s'exécuter avant le fetch
          await new Promise(r => setTimeout(r, 1200));
          const r = await axios.get(`${API}/discovery/team-insights/reports/${reportId}`, { headers: authHeaders() });
          setUnlockedReport(r.data);
          setPreview(null);
          setShowUnlockModal(false);
          toast.success('Rapport déverrouillé avec succès');
        } catch (e) {
          if (e?.response?.status === 402) {
            toast.info('Traitement du paiement en cours — réessayez dans 5 secondes.');
          } else {
            toast.error(e?.response?.data?.detail || 'Impossible de charger le rapport.');
          }
        } finally {
          setLoading(false);
          // Nettoie l'URL
          window.history.replaceState({}, '', window.location.pathname);
        }
      })();
    }
  }, []);

  const analyze = useCallback(async () => {
    setLoading(true);
    try {
      if (parsedEmails.length < 3) {
        toast.error(`Minimum 3 emails (actuellement ${parsedEmails.length})`);
        setLoading(false);
        return;
      }
      const payload = { collaborator_emails: parsedEmails };
      if (memberNames.length && memberNames.some(n => (n || '').trim())) {
        payload.member_names = memberNames;
      }
      const r = await axios.post(`${API}/discovery/team-insights/analyze`, payload,
        { headers: authHeaders() });
      setPreview(r.data);
      setUnlockedReport(null);
      setShowUnlockModal(true);
      if ((r.data.unresolved_emails || []).length > 0) {
        toast.info(`${r.data.unresolved_emails.length} email(s) sans profil Discovery payé`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Analyse échouée');
    } finally {
      setLoading(false);
    }
  }, [parsedEmails, memberNames]);

  const unlockReport = useCallback(async () => {
    if (!preview?.report_id) return;
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const r = await axios.post(`${API}/discovery/team-insights/checkout`,
        { report_id: preview.report_id, origin_url: origin },
        { headers: authHeaders() });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Création du paiement impossible');
      setCheckoutLoading(false);
    }
  }, [preview]);

  const downloadPdf = useCallback(async () => {
    const reportId = unlockedReport?.report_id;
    if (!reportId) return;
    setPdfLoading(true);
    try {
      const resp = await axios.get(
        `${API}/discovery/team-insights/reports/${reportId}/pdf`,
        { headers: authHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `coachdia-team-insights-${String(reportId).slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Rapport PDF téléchargé');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        toast.error('Paiement requis pour télécharger ce rapport.');
      } else if (status === 403) {
        toast.error('Accès refusé à ce rapport.');
      } else {
        toast.error('Impossible de générer le PDF. Réessayez.');
      }
    } finally {
      setPdfLoading(false);
    }
  }, [unlockedReport]);

  const inviteUnresolved = useCallback(async () => {
    if (!preview?.report_id || !(preview?.unresolved_emails || []).length) return;
    setInviteLoading(true);
    try {
      const r = await axios.post(
        `${API}/discovery/team-insights/invite-unresolved`,
        {
          report_id: preview.report_id,
          emails: preview.unresolved_emails,
          origin_url: window.location.origin,
        },
        { headers: authHeaders() }
      );
      const sent = r.data?.sent_count || 0;
      const failed = r.data?.failed_count || 0;
      if (sent > 0) {
        toast.success(`${sent} invitation${sent > 1 ? 's' : ''} envoyée${sent > 1 ? 's' : ''}${failed ? ` (${failed} échec)` : ''}`);
      } else {
        toast.error('Aucune invitation envoyée. Vérifiez les emails.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Envoi impossible.');
    } finally {
      setInviteLoading(false);
    }
  }, [preview]);

  const sendReportByEmail = useCallback(async () => {
    const reportId = unlockedReport?.report_id;
    if (!reportId) return;
    const recipient = (emailRecipient || user?.email || '').trim();
    if (!recipient || !recipient.includes('@')) {
      toast.error('Email destinataire invalide.');
      return;
    }
    setEmailLoading(true);
    try {
      await axios.post(
        `${API}/discovery/team-insights/reports/${reportId}/email`,
        { recipient, message: emailMessage?.trim() || undefined },
        { headers: authHeaders() }
      );
      toast.success(`Rapport envoyé à ${recipient}`);
      setEmailDialogOpen(false);
      setEmailMessage('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Envoi échoué.');
    } finally {
      setEmailLoading(false);
    }
  }, [unlockedReport, emailRecipient, emailMessage, user]);


  // Pré-remplir l'email destinataire par défaut
  React.useEffect(() => {
    if (user?.email && !emailRecipient) setEmailRecipient(user.email);
  }, [user, emailRecipient]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <Card className="p-8 max-w-md text-center">
          <p className="text-stone-700">COACHDIA Pulse nécessite d'être connecté.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="team-insights-page">
      {/* ═══ Barre de navigation premium (sortie claire) ═══ */}
      <div className="sticky top-0 z-40 bg-stone-50/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/training'))}
            data-testid="team-insights-back-btn"
            className="flex items-center gap-2 text-[13px] text-[#4B5563] hover:text-[#0F0F10] transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <button
            onClick={() => navigate('/training')}
            data-testid="team-insights-home-btn"
            className="flex items-center gap-2 text-[13px] text-[#92710A] hover:text-[#0F0F10] transition"
          >
            <Home className="w-4 h-4" />
            Tableau de bord
          </button>
        </div>
      </div>      <div className="bg-[#0F0F10] text-white py-4">
        <div className="max-w-6xl mx-auto px-6 flex items-center gap-3">
          <Users className="w-5 h-5 text-[#92710A]" />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C4B27A]">COACHDIA Pulse · Rapport groupe</div>
            <div className="font-serif text-lg">Comprenez la dynamique comportementale de votre équipe</div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
        {/* Composer */}
        <aside className="lg:col-span-1 space-y-6">
          <Card className="p-6">
            <h2 className="font-serif text-xl text-stone-900 mb-3">Composez votre équipe</h2>
            <p className="text-xs text-stone-500 mb-3">
              Collez les emails de vos collaborateurs (3 minimum, un par ligne ou séparés par virgule). Ils doivent avoir un profil Prism payé.
            </p>
            <textarea
              data-testid="team-insights-emails-textarea"
              value={emailsInput}
              onChange={(e) => setEmailsInput(e.target.value)}
              placeholder="marie@entreprise.com&#10;pierre@entreprise.com&#10;sophie@entreprise.com"
              className="w-full min-h-[180px] px-3 py-2 border border-stone-300 rounded-sm text-sm focus:border-[#92710A] focus:outline-none font-mono"
            />
            <div className="text-xs text-stone-500 mt-2" data-testid="team-insights-emails-count">
              {parsedEmails.length} email(s) détecté(s)
            </div>

            <Button
              onClick={analyze}
              disabled={loading || parsedEmails.length < 3}
              data-testid="team-insights-analyze-btn"
              className="w-full mt-5 bg-[#0F0F10] hover:bg-stone-800 text-white rounded-sm"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse...</>
                : <>Analyser l'équipe <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </Card>

          <Card className="p-5 border-[#92710A] bg-[#FBF9F5]">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-2">Produit Team B2B</div>
            <p className="text-sm text-stone-800 leading-relaxed">
              Paiement à l'unité selon la taille d'équipe. Promos automatiques quand actives.
            </p>
            <ul className="mt-3 text-xs text-stone-700 space-y-1">
              <li>• Starter (3-5) — 49€</li>
              <li>• Team (6-10) — 99€</li>
              <li>• Business (11-20) — 199€</li>
              <li>• Enterprise (21-50) — 399€</li>
              <li>• Custom (50+) — devis</li>
            </ul>
          </Card>
        </aside>

        {/* Results */}
        <main className="lg:col-span-2 space-y-8">
          {!preview && !unlockedReport && !loading && (
            <Card className="p-10 text-center border-dashed" data-testid="team-insights-empty">
              <Users className="w-10 h-10 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-600">Saisissez les emails de votre équipe puis lancez l'analyse.</p>
            </Card>
          )}

          {preview && !unlockedReport && (
            <div data-testid="team-insights-preview-locked">
              <Card className="p-6 border-[#92710A]/40 bg-gradient-to-br from-white to-[#FBF9F5]">
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="w-4 h-4 text-[#92710A]" />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#92710A]">Aperçu verrouillé · {preview.team_size} profils</span>
                </div>
                <h2 className="font-serif text-3xl text-stone-900 mb-2">
                  Dominante collective : {COLOR_META[preview.team_dominant]?.label}
                </h2>
                <p className="text-stone-700 leading-relaxed italic">
                  {preview.narrative_teaser} …
                </p>
                <div className="mt-5 grid grid-cols-4 gap-3">
                  {['red', 'yellow', 'green', 'blue'].map(c => (
                    <div key={c} className="text-center p-3 border border-stone-200 rounded-sm bg-white">
                      <div className="w-3 h-3 mx-auto rounded-full mb-1" style={{ backgroundColor: COLOR_META[c].hex }} />
                      <div className="text-xs text-stone-500">{COLOR_META[c].label}</div>
                      <div className="font-serif text-lg text-stone-900">{Math.round(preview.distribution?.[c] || 0)}</div>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => setShowUnlockModal(true)}
                  data-testid="team-insights-open-unlock"
                  className="w-full mt-6 bg-[#0F0F10] hover:bg-stone-800 text-white rounded-sm"
                >
                  Débloquer le rapport complet <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
              {(preview.unresolved_emails || []).length > 0 && (
                <Card className="mt-4 p-5 border-amber-300 bg-amber-50" data-testid="team-insights-unresolved-block">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-amber-700 mb-1 font-semibold">
                    {preview.unresolved_emails.length} email(s) sans profil Prism
                  </div>
                  <p className="text-xs text-amber-800 mb-3">
                    Ces collaborateurs n'ont pas encore de profil Prism payé : {preview.unresolved_emails.join(', ')}
                  </p>
                  <div className="p-3 rounded-sm bg-white border border-amber-200 flex items-center justify-between gap-4">
                    <div className="text-xs text-stone-700 flex-1">
                      <div className="font-semibold text-stone-900 mb-0.5">Invitez-les à compléter leur profil</div>
                      <div>Chaque invitation débloque un profil manquant pour enrichir votre analyse collective.</div>
                    </div>
                    <Button
                      onClick={inviteUnresolved}
                      disabled={inviteLoading}
                      data-testid="team-insights-invite-unresolved-btn"
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-4 shrink-0"
                    >
                      {inviteLoading
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</>
                        : <><Send className="w-4 h-4 mr-2" /> Inviter {preview.unresolved_emails.length} collaborateur{preview.unresolved_emails.length > 1 ? 's' : ''}</>}
                    </Button>
                  </div>
                </Card>
              )}
            </div>
          )}

          {unlockedReport && (
            <div data-testid="team-insights-unlocked-report">
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="w-4 h-4 text-emerald-600" />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-emerald-700">Rapport déverrouillé · {unlockedReport.team_size} profils</span>
                </div>
                <h2 className="font-serif text-3xl text-stone-900 mb-3">
                  Dominante collective : {COLOR_META[unlockedReport.report_data?.team_dominant]?.label}
                </h2>
                <p className="text-stone-700 leading-relaxed">{unlockedReport.report_data?.narrative}</p>

                {/* ═══ BOUTON TÉLÉCHARGEMENT PDF PREMIUM + ENVOI EMAIL ═══ */}
                <div className="mt-6 p-5 rounded-sm bg-gradient-to-br from-[#FBF8F1] to-[#F5EFE1] border border-[#92710A]/30">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-1">Rapport premium · prêt à partager</div>
                      <div className="font-serif text-lg text-stone-900">Téléchargez ou partagez le rapport</div>
                      <div className="text-xs text-stone-600 mt-1">PDF complet · Cartographie · Synergies · Frictions · Scripts managériaux</div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        onClick={downloadPdf}
                        disabled={pdfLoading}
                        data-testid="team-insights-download-pdf-btn"
                        className="bg-[#0F0F10] hover:bg-stone-800 text-white rounded-full px-5 shrink-0"
                      >
                        {pdfLoading
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération...</>
                          : <><Download className="w-4 h-4 mr-2" /> Télécharger le PDF</>}
                      </Button>
                      <Button
                        onClick={() => setEmailDialogOpen(true)}
                        data-testid="team-insights-email-report-btn"
                        variant="outline"
                        className="border-[#92710A] text-[#92710A] hover:bg-[#FBF8F1] rounded-full px-5 shrink-0"
                      >
                        <Mail className="w-4 h-4 mr-2" /> Envoyer par email
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-6 mt-4">
                <h3 className="font-serif text-xl mb-3">Cartographie complète</h3>
                <div className="grid grid-cols-4 gap-3">
                  {['red', 'yellow', 'green', 'blue'].map(c => (
                    <div key={c} className="text-center p-3 border border-stone-200 rounded-sm bg-white">
                      <div className="w-3 h-3 mx-auto rounded-full mb-1" style={{ backgroundColor: COLOR_META[c].hex }} />
                      <div className="text-xs text-stone-500">{COLOR_META[c].label}</div>
                      <div className="font-serif text-lg text-stone-900">{Math.round(unlockedReport.report_data?.distribution?.[c] || 0)}</div>
                    </div>
                  ))}
                </div>
              </Card>

              {(unlockedReport.report_data?.risks || []).length > 0 && (
                <Card className="p-6 mt-4">
                  <h3 className="font-serif text-xl mb-3">Risques organisationnels</h3>
                  <ul className="space-y-2">
                    {unlockedReport.report_data.risks.map((r, i) => (
                      <li key={i} className="p-3 border-l-2 border-amber-400 bg-amber-50">
                        <div className="font-semibold text-sm">{r.title}</div>
                        <div className="text-xs text-stone-700 mt-1">{r.detail}</div>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {(unlockedReport.report_data?.levers || []).length > 0 && (
                <Card className="p-6 mt-4">
                  <h3 className="font-serif text-xl mb-3">Leviers d'action</h3>
                  <ul className="space-y-2">
                    {unlockedReport.report_data.levers.map((l, i) => (
                      <li key={i} className="p-3 border border-emerald-300 bg-emerald-50 rounded-sm text-sm">{l}</li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          )}
        </main>
      </div>

      {/* ═══ MODALE DE DÉVERROUILLAGE PREMIUM — CONVERSION (iter236) ═══ */}
      {showUnlockModal && preview && preview.pack && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.78)', backdropFilter: 'blur(8px)' }}
          onClick={() => !checkoutLoading && setShowUnlockModal(false)}
          data-testid="team-insights-unlock-modal"
        >
          <div
            className="relative w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl bg-white max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── HERO — tension narrative (storytelling) ── */}
            <div className="bg-gradient-to-br from-[#2A2319] via-[#3D2F1C] to-[#4A3620] text-white p-6">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#F3D060]">
                <Sparkles className="w-3.5 h-3.5" /> Pack {preview.pack.name}
              </div>
              <h3 className="font-serif text-2xl mt-1 leading-tight">
                Votre équipe a <span className="text-[#F3D060]">des signaux invisibles</span> qui freinent sa performance.
              </h3>
              <p className="text-[#D9C98D] text-sm mt-2 leading-relaxed">
                {preview.tensions_preview?.tensions_count > 0
                  ? `${preview.tensions_preview.tensions_count} friction${preview.tensions_preview.tensions_count > 1 ? 's' : ''} comportementale${preview.tensions_preview.tensions_count > 1 ? 's' : ''} détectée${preview.tensions_preview.tensions_count > 1 ? 's' : ''} entre vos ${preview.team_size} profils — sans rapport, vous continuerez de les subir.`
                  : `Une dominante ${COLOR_META[preview.team_dominant]?.label} installe déjà une lecture biaisée de votre ${preview.team_size}-équipe.`}
              </p>
            </div>

            <div className="p-6 space-y-5">
              {/* ── 3 SIGNAUX DÉTECTÉS (teasers anonymisés) ── */}
              {preview.tensions_preview && (
                <div data-testid="team-insights-tensions-preview" className="border border-stone-200 rounded-lg p-4 bg-stone-50">
                  <div className="text-[10px] tracking-widest uppercase text-stone-500 mb-3 font-semibold">
                    3 signaux détectés dans votre équipe
                  </div>
                  <div className="space-y-3">
                    {preview.tensions_preview.tension_teaser && (
                      <div className="flex items-start gap-3">
                        <Flame className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-stone-500">Tension</div>
                          <div className="text-sm text-stone-800">{preview.tensions_preview.tension_teaser}</div>
                        </div>
                      </div>
                    )}
                    {preview.tensions_preview.imbalance_teaser && (
                      <div className="flex items-start gap-3">
                        <Zap className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-stone-500">Déséquilibre</div>
                          <div className="text-sm text-stone-800">{preview.tensions_preview.imbalance_teaser}</div>
                        </div>
                      </div>
                    )}
                    {preview.tensions_preview.risk_teaser && (
                      <div className="flex items-start gap-3">
                        <Eye className="w-4 h-4 text-[#92710A] mt-0.5 shrink-0" />
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-stone-500">Angle mort</div>
                          <div className="text-sm text-stone-800">{preview.tensions_preview.risk_teaser}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-stone-200 text-[11px] italic text-stone-500">
                    Le rapport complet nomme chaque profil, précise les dynamiques et fournit les scripts managériaux.
                  </div>
                </div>
              )}

              {/* ── PROJECTION COÛT (tension business) ── */}
              {preview.tensions_preview?.estimated_monthly_cost_eur > 0 && (
                <div data-testid="team-insights-cost-projection" className="rounded-lg p-4 bg-gradient-to-br from-red-50 to-amber-50 border border-red-200">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-red-700" />
                    <span className="text-[10px] tracking-widest uppercase text-red-700 font-semibold">Coût estimé des frictions non traitées</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif text-3xl text-red-900">~ {preview.tensions_preview.estimated_monthly_cost_eur.toLocaleString('fr-FR')}€</span>
                    <span className="text-sm text-red-700">/ mois</span>
                  </div>
                  <div className="text-xs text-stone-700 mt-1">
                    Basé sur {preview.team_size} profils · perte de productivité estimée (littérature RH).
                  </div>
                </div>
              )}

              {/* ── CE QUE VOUS DÉBLOQUEZ (valeur explicite) ── */}
              <div className="border-t border-stone-200 pt-4">
                <div className="text-[10px] tracking-widest uppercase text-stone-500 mb-2">Ce que vous débloquez immédiatement</div>
                <ul className="text-sm text-stone-700 space-y-1.5">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Rapport PDF complet (ADN équipe + 10 sections éditoriales)</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Synergies et frictions <strong>nommées</strong> (qui avec qui, qui bloque qui)</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Guide managérial individuel (1 page par membre, prêt pour vos 1:1)</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Scripts managériaux à copier-coller (recadrer, motiver, trancher)</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> Recommandations actionnables avec porteur désigné</li>
                </ul>
              </div>

              <div className="flex items-center justify-between border-t border-stone-200 pt-4 flex-wrap gap-3">
                <div>
                  {preview.pack.promo_effective ? (
                    <>
                      <span className="text-3xl font-serif text-[#92710A]">{preview.pack.effective_price_eur}€</span>
                      <span className="ml-2 text-stone-400 line-through">{preview.pack.base_price_eur}€</span>
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#F3D060] text-[#2A2319] font-semibold">-{preview.pack.discount_pct}%</span>
                    </>
                  ) : (
                    <span className="text-3xl font-serif text-stone-900">{preview.pack.effective_price_eur}€</span>
                  )}
                  <div className="text-[11px] text-stone-500 mt-0.5">Paiement unique · accès illimité au rapport</div>
                </div>
                <Button
                  onClick={unlockReport}
                  disabled={checkoutLoading}
                  data-testid="team-insights-unlock-cta"
                  className="bg-[#0F0F10] hover:bg-stone-800 text-white rounded-full px-6"
                >
                  {checkoutLoading
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirection...</>
                    : <>Débloquer pour {preview.pack.effective_price_eur}€ <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
              </div>

              <button
                onClick={() => setShowUnlockModal(false)}
                disabled={checkoutLoading}
                data-testid="team-insights-unlock-close"
                className="w-full text-xs text-stone-500 hover:text-stone-700 py-2"
              >
                Plus tard — je reste sur la preview verrouillée
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODALE ENVOI EMAIL DU RAPPORT (iter236) ═══ */}
      {emailDialogOpen && unlockedReport && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.78)', backdropFilter: 'blur(8px)' }}
          onClick={() => !emailLoading && setEmailDialogOpen(false)}
          data-testid="team-insights-email-modal"
        >
          <div
            className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-[#2A2319] to-[#3D2F1C] text-white p-5">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#F3D060]">
                <Mail className="w-3.5 h-3.5" /> Partager le rapport
              </div>
              <h3 className="font-serif text-xl mt-1">Envoyer le PDF par email</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] tracking-widest uppercase text-stone-500 mb-1 block">Destinataire</label>
                <input
                  type="email"
                  data-testid="team-insights-email-recipient-input"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  placeholder="nom@entreprise.com"
                  className="w-full px-3 py-2 border border-stone-300 rounded-sm text-sm focus:border-[#92710A] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-widest uppercase text-stone-500 mb-1 block">Message personnel (optionnel)</label>
                <textarea
                  data-testid="team-insights-email-message-input"
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="« J'aimerais discuter de ce rapport avec toi cette semaine. »"
                  rows={3}
                  className="w-full px-3 py-2 border border-stone-300 rounded-sm text-sm focus:border-[#92710A] focus:outline-none"
                />
              </div>
              <div className="flex items-center justify-between gap-3 pt-2">
                <button
                  onClick={() => setEmailDialogOpen(false)}
                  disabled={emailLoading}
                  data-testid="team-insights-email-cancel"
                  className="text-sm text-stone-500 hover:text-stone-700 px-3 py-2"
                >
                  Annuler
                </button>
                <Button
                  onClick={sendReportByEmail}
                  disabled={emailLoading}
                  data-testid="team-insights-email-send-btn"
                  className="bg-[#0F0F10] hover:bg-stone-800 text-white rounded-full px-5"
                >
                  {emailLoading
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</>
                    : <><Send className="w-4 h-4 mr-2" /> Envoyer le PDF</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
